import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Plane, ArrowLeftRight, Loader2, Plus, X, CalendarDays, Users,
    ChevronDown, Clock3, Sparkles, ShieldCheck,
} from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Sections5";
import { fetchFlightMeta, searchAirports, searchFlights, formatApiError } from "@/lib/api";

const TRIP_TABS = [
    ["one_way", "One Way"],
    ["round_trip", "Round Trip"],
    ["multi_city", "Multi-City"],
];
const CABINS = [
    ["economy", "Economy"], ["premium_economy", "Premium Economy"],
    ["business", "Business"], ["first", "First Class"],
];
const RECENT_KEY = "gc_flight_recent";
const todayStr = () => new Date().toISOString().slice(0, 10);
let _popularCache = null; // module-level: the empty-query (popular) list never changes per visit

function AirportPicker({ label, value, onChange, exclude, testid }) {
    const [query, setQuery] = useState(value ? `${value.city} (${value.code})` : "");
    const [opts, setOpts] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const box = useRef(null);
    const timer = useRef(null);

    useEffect(() => { setQuery(value ? `${value.city} (${value.code})` : ""); }, [value]);
    useEffect(() => {
        const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const type = (q) => {
        setQuery(q);
        setOpen(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            setBusy(true);
            try { setOpts((await searchAirports(q)).airports); }
            catch { setOpts([]); }
            finally { setBusy(false); }
        }, 220);
    };

    return (
        <div className="relative flex-1 min-w-0" ref={box}>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">{label}</label>
            <input
                data-testid={testid}
                value={query}
                onFocus={async () => {
                    setOpen(true);
                    if (!opts.length) {
                        try {
                            if (!_popularCache) _popularCache = (await searchAirports("")).airports;
                            setOpts(_popularCache);
                        } catch { /* ignore */ }
                    }
                }}
                onChange={(e) => { type(e.target.value); if (!e.target.value) onChange(null); }}
                placeholder="City or airport"
                className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-navy focus:outline-none focus:ring-2 focus:ring-saffron/60"
            />
            {open && (
                <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-stone-200 bg-white shadow-xl" data-testid={`${testid}-options`}>
                    {busy && <div className="px-3 py-2 text-xs text-stone-400 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Searching…</div>}
                    {!busy && !opts.length && <div className="px-3 py-2 text-xs text-stone-400">No airports found</div>}
                    {opts.filter((a) => a.code !== exclude).map((a) => (
                        <button key={a.code} type="button"
                            data-testid={`${testid}-opt-${a.code}`}
                            onClick={() => { onChange(a); setQuery(`${a.city} (${a.code})`); setOpen(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-cream flex items-center gap-3">
                            <span className="font-display font-black text-saffron text-sm w-10">{a.code}</span>
                            <span className="min-w-0">
                                <span className="block text-sm font-semibold text-navy truncate">{a.city}</span>
                                <span className="block text-[11px] text-stone-400 truncate">{a.name}</span>
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function PaxPicker({ pax, setPax }) {
    const [open, setOpen] = useState(false);
    const box = useRef(null);
    useEffect(() => {
        const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);
    const rows = [["adults", "Adults", "12+ yrs", 1], ["children", "Children", "2–11 yrs", 0], ["infants", "Infants", "Under 2 yrs", 0]];
    const total = pax.adults + pax.children + pax.infants;
    return (
        <div className="relative" ref={box}>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">Travellers</label>
            <button type="button" data-testid="pax-picker-btn" onClick={() => setOpen(!open)}
                className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-navy flex items-center justify-between">
                <span className="flex items-center gap-2"><Users className="h-4 w-4 text-saffron" />{total} Traveller{total > 1 ? "s" : ""}</span>
                <ChevronDown className="h-4 w-4 text-stone-400" />
            </button>
            {open && (
                <div className="absolute z-30 mt-1 w-full sm:w-72 rounded-xl border border-stone-200 bg-white shadow-xl p-3 space-y-2" data-testid="pax-picker-panel">
                    {rows.map(([k, label, sub, min]) => (
                        <div key={k} className="flex items-center justify-between">
                            <div><p className="text-sm font-bold text-navy">{label}</p><p className="text-[11px] text-stone-400">{sub}</p></div>
                            <div className="flex items-center gap-2">
                                <button type="button" data-testid={`pax-${k}-minus`}
                                    onClick={() => setPax({ ...pax, [k]: Math.max(min, pax[k] - 1), infants: k === "adults" ? Math.min(pax.infants, pax[k] - 1) : pax.infants })}
                                    className="h-7 w-7 rounded-full border border-stone-300 text-navy font-black hover:border-saffron">−</button>
                                <span className="w-5 text-center text-sm font-bold text-navy" data-testid={`pax-${k}-count`}>{pax[k]}</span>
                                <button type="button" data-testid={`pax-${k}-plus`}
                                    onClick={() => setPax({ ...pax, [k]: Math.min(k === "infants" ? pax.adults : 9, pax[k] + 1) })}
                                    className="h-7 w-7 rounded-full border border-stone-300 text-navy font-black hover:border-saffron">+</button>
                            </div>
                        </div>
                    ))}
                    <p className="text-[11px] text-stone-400 pt-1 border-t border-stone-100">Max 9 travellers · every infant needs an adult</p>
                </div>
            )}
        </div>
    );
}

export default function FlightSearchPage() {
    const navigate = useNavigate();
    const [meta, setMeta] = useState(null);
    const [tripType, setTripType] = useState("one_way");
    const blankLeg = () => ({ origin: null, destination: null, date: "" });
    const [legs, setLegs] = useState([blankLeg(), blankLeg()]);
    const [pax, setPax] = useState({ adults: 1, children: 0, infants: 0 });
    const [cabin, setCabin] = useState("economy");
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        fetchFlightMeta().then(setMeta).catch(() => setMeta({ demo_mode: true, booking_enabled: false }));
        try { setRecent(JSON.parse(localStorage.getItem(RECENT_KEY) || "[]")); } catch { setRecent([]); }
    }, []);

    const activeLegs = useMemo(() => (tripType === "multi_city" ? legs : legs.slice(0, tripType === "round_trip" ? 2 : 1)), [tripType, legs]);

    const setLeg = (i, patch) => setLegs((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
    const swap = (i) => setLeg(i, { origin: legs[i].destination, destination: legs[i].origin });

    const validate = () => {
        const e = {};
        activeLegs.forEach((l, i) => {
            if (!l.origin) e[`o${i}`] = "Pick a departure city";
            if (!l.destination) e[`d${i}`] = "Pick a destination";
            if (l.origin && l.destination && l.origin.code === l.destination.code) e[`d${i}`] = "Origin and destination can't match";
            if (!l.date) e[`dt${i}`] = "Pick a date";
            else if (l.date < todayStr()) e[`dt${i}`] = "Date can't be in the past";
            else if (i > 0 && activeLegs[i - 1].date && l.date < activeLegs[i - 1].date) e[`dt${i}`] = "Legs must be in order";
        });
        if (pax.adults + pax.children + pax.infants > 9) e.pax = "Maximum 9 travellers";
        if (pax.infants > pax.adults) e.pax = "Every infant needs an adult";
        setErrors(e);
        return !Object.keys(e).length;
    };

    const submit = async () => {
        if (!validate()) { toast.error("Please fix the highlighted fields"); return; }
        setBusy(true);
        try {
            const payload = {
                trip_type: tripType,
                legs: activeLegs.map((l) => ({ origin: l.origin.code, destination: l.destination.code, date: l.date })),
                ...pax, cabin,
            };
            const res = await searchFlights(payload);
            const label = activeLegs.map((l) => `${l.origin.code}→${l.destination.code}`).join(" · ");
            const rec = [{ label, trip_type: tripType, legs: payload.legs, pax, cabin, at: Date.now() },
                ...recent.filter((r) => r.label !== label)].slice(0, 4);
            localStorage.setItem(RECENT_KEY, JSON.stringify(rec));
            navigate(`/flights/results/${res.search_id}`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    const loadRecent = (r) => {
        setTripType(r.trip_type);
        setPax(r.pax); setCabin(r.cabin);
        const rebuilt = r.legs.map((l) => ({ origin: { code: l.origin, city: l.origin }, destination: { code: l.destination, city: l.destination }, date: l.date }));
        while (rebuilt.length < 2) rebuilt.push(blankLeg());
        setLegs(rebuilt);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <div className="min-h-screen bg-cream" data-testid="flight-search-page">
            <Header />
            <section className="bg-navy text-cream relative overflow-hidden">
                <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-saffron/10 blur-3xl" />
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20 relative">
                    <p className="font-script text-3xl sm:text-4xl text-gold">Har Safar, Ek Kahani</p>
                    <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-tight">
                        Flights, booked the <span className="text-saffron">GhumneChaley</span> way.
                    </h1>
                    <p className="mt-4 text-cream/80 text-base sm:text-lg max-w-2xl">
                        Search domestic and international flights, see the full fare upfront, and let our team
                        ticket your journey with the care we give every group trip.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-4 text-xs text-cream/70">
                        <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-gold" />Transparent fares, no hidden fees</span>
                        <span className="flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-gold" />Human support on every booking</span>
                    </div>
                </div>
            </section>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 -mt-2 pb-20">
                {meta?.demo_mode && (
                    <div className="mt-6 rounded-xl border border-gold/60 bg-gold/15 px-4 py-3 text-sm text-navy flex gap-2 items-start" data-testid="demo-mode-banner">
                        <Sparkles className="h-4 w-4 mt-0.5 text-saffron shrink-0" />
                        <span><b>Preview mode:</b> flight results currently show <b>demonstration data</b> while our
                            certified flight supplier integration is being finalised. Sample fares are not live offers and
                            online booking is disabled.</span>
                    </div>
                )}

                <div className="mt-6 rounded-2xl border border-stone-300 bg-white shadow-xl overflow-hidden" data-testid="flight-search-card">
                    <div className="flex border-b border-stone-200">
                        {TRIP_TABS.map(([k, label]) => (
                            <button key={k} data-testid={`trip-tab-${k}`} onClick={() => setTripType(k)}
                                className={`flex-1 px-2 py-3.5 text-xs sm:text-sm font-bold uppercase tracking-wide transition-colors ${tripType === k ? "text-saffron border-b-2 border-saffron bg-cream/60" : "text-stone-500 hover:text-navy"}`}>
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="p-4 sm:p-6 space-y-4">
                        {activeLegs.map((leg, i) => (
                            <div key={i} className="rounded-xl border border-stone-200 bg-cream/40 p-3 sm:p-4">
                                {tripType === "multi_city" && (
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[11px] font-black uppercase tracking-wider text-navy/60">Flight {i + 1}</span>
                                        {legs.length > 2 && (
                                            <button type="button" data-testid={`remove-leg-${i}`} onClick={() => setLegs(legs.filter((_, idx) => idx !== i))}
                                                className="text-stone-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                                        )}
                                    </div>
                                )}
                                <div className="flex flex-col md:flex-row gap-3 md:items-end">
                                    <div className="flex flex-1 items-end gap-2">
                                        <AirportPicker label={tripType === "round_trip" && i === 1 ? "From (return)" : "From"} value={leg.origin}
                                            exclude={leg.destination?.code}
                                            onChange={(a) => setLeg(i, { origin: a })} testid={`leg-${i}-origin`} />
                                        <button type="button" data-testid={`swap-leg-${i}`} onClick={() => swap(i)} title="Swap"
                                            className="mb-1 shrink-0 h-9 w-9 rounded-full border border-stone-300 bg-white flex items-center justify-center text-navy hover:border-saffron hover:text-saffron transition-colors">
                                            <ArrowLeftRight className="h-4 w-4" />
                                        </button>
                                        <AirportPicker label="To" value={leg.destination} exclude={leg.origin?.code}
                                            onChange={(a) => setLeg(i, { destination: a })} testid={`leg-${i}-destination`} />
                                    </div>
                                    <div className="md:w-44">
                                        <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">
                                            {tripType === "round_trip" ? (i === 0 ? "Departure" : "Return") : "Departure"}
                                        </label>
                                        <div className="relative">
                                            <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-saffron pointer-events-none" />
                                            <input type="date" data-testid={`leg-${i}-date`} min={todayStr()} value={leg.date}
                                                onChange={(e) => setLeg(i, { date: e.target.value })}
                                                className="w-full rounded-xl border border-stone-300 bg-white pl-9 pr-3 py-2.5 text-sm font-semibold text-navy focus:outline-none focus:ring-2 focus:ring-saffron/60" />
                                        </div>
                                    </div>
                                </div>
                                {(errors[`o${i}`] || errors[`d${i}`] || errors[`dt${i}`]) && (
                                    <p className="mt-2 text-xs font-semibold text-red-600" data-testid={`leg-${i}-error`}>
                                        {errors[`o${i}`] || errors[`d${i}`] || errors[`dt${i}`]}
                                    </p>
                                )}
                            </div>
                        ))}

                        {tripType === "multi_city" && legs.length < 4 && (
                            <button type="button" data-testid="add-leg-btn" onClick={() => setLegs([...legs, blankLeg()])}
                                className="flex items-center gap-2 text-sm font-bold text-saffron hover:text-navy transition-colors">
                                <Plus className="h-4 w-4" /> Add another flight
                            </button>
                        )}

                        <div className="grid sm:grid-cols-2 gap-3">
                            <PaxPicker pax={pax} setPax={setPax} />
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">Cabin Class</label>
                                <select data-testid="cabin-select" value={cabin} onChange={(e) => setCabin(e.target.value)}
                                    className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-navy focus:outline-none focus:ring-2 focus:ring-saffron/60">
                                    {CABINS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                                </select>
                            </div>
                        </div>
                        {errors.pax && <p className="text-xs font-semibold text-red-600" data-testid="pax-error">{errors.pax}</p>}

                        <button data-testid="flight-search-submit" onClick={submit} disabled={busy}
                            className="w-full rounded-full bg-saffron text-white font-display font-black text-lg py-3.5 hover:bg-navy transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                            {busy ? <><Loader2 className="h-5 w-5 animate-spin" />Searching flights…</> : <><Plane className="h-5 w-5" />Search Flights</>}
                        </button>
                    </div>
                </div>

                {recent.length > 0 && (
                    <div className="mt-8" data-testid="recent-searches">
                        <p className="text-xs font-bold uppercase tracking-wider text-stone-500 flex items-center gap-2"><Clock3 className="h-3.5 w-3.5" />Recent searches</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {recent.map((r) => (
                                <button key={r.at} data-testid={`recent-${r.at}`} onClick={() => loadRecent(r)}
                                    className="rounded-full border border-stone-300 bg-white px-4 py-2 text-xs font-semibold text-navy hover:border-saffron hover:text-saffron transition-colors">
                                    {r.label} · {r.trip_type.replace("_", " ")}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}
